import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { useConfirmDialogStore } from '@/shared/stores/confirmDialogStore';
import { useLingui } from '@lingui/react/macro';

/**
 * The single confirm-dialog surface, mounted once at the app root beside the
 * toast surface. Driven by {@link useConfirmDialogStore} via the {@link confirm}
 * façade — no per-caller state. Cancel, Escape, and overlay click all resolve
 * the awaited promise `false`; the confirm button resolves it `true`. Default
 * labels are localized here (a lib helper can't run the `useLingui` hook).
 */
export function ConfirmDialogHost() {
  const { t } = useLingui();
  const open = useConfirmDialogStore((s) => s.open);
  const options = useConfirmDialogStore((s) => s.options);
  const respond = useConfirmDialogStore((s) => s.respond);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) respond(false);
      }}
    >
      <DialogContent className="sm:max-w-md">
        {options ? (
          <>
            <DialogHeader>
              <DialogTitle>{options.title}</DialogTitle>
              {options.description ? (
                <DialogDescription>{options.description}</DialogDescription>
              ) : null}
            </DialogHeader>
            {options.items && options.items.length > 0 ? (
              <ul className="scrollbar-thin-stable max-h-48 divide-y overflow-y-auto overscroll-contain rounded-md border text-sm">
                {options.items.map((item, index) => (
                  <li key={`${index}:${item}`} className="break-all px-3 py-2 font-mono text-xs">
                    {item}
                  </li>
                ))}
              </ul>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => respond(false)}>
                {options.cancelLabel ?? t`Cancel`}
              </Button>
              <Button
                type="button"
                variant={options.variant ?? 'default'}
                onClick={() => respond(true)}
              >
                {options.confirmLabel ?? t`Confirm`}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
